/**
 * On-demand Facebook JS SDK for the WhatsApp Embedded Signup.
 *
 * The SDK used to be bootstrapped in index.html with a single, hardcoded app id. Cloud API
 * channels on the Solution Partner's credit line are onboarded through a different Meta app, so
 * the app is now chosen per channel: the SDK is loaded once and `FB.init` is called again
 * whenever the requested app differs from the one currently initialized.
 *
 * FB.login must run synchronously inside the click handler, or the browser blocks its popup.
 * That is why loading is split in two: `prepareFacebookSdk` (async, called ahead of time) and
 * `facebookSdkFor` (sync, called from the click).
 */

declare var FB: any;

const SDK_ELEMENT_ID = 'facebook-jssdk';
const SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';
const GRAPH_API_VERSION = 'v26.0';

let sdkLoading: Promise<void> | null = null;
let initializedAppId: string | null = null;

function isSdkLoaded(): boolean {
  return typeof FB !== 'undefined' && !!FB;
}

function loadSdk(): Promise<void> {
  if (isSdkLoaded()) {
    return Promise.resolve();
  }
  if (!sdkLoading) {
    sdkLoading = new Promise<void>((resolve, reject) => {
      (window as any).fbAsyncInit = () => resolve();
      const script = document.createElement('script');
      script.id = SDK_ELEMENT_ID;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = SDK_URL;
      script.onerror = () => {
        // Allow a later attempt (e.g. a transient network failure or a blocker being disabled).
        sdkLoading = null;
        script.remove();
        reject(new Error('Facebook SDK failed to load'));
      };
      document.body.appendChild(script);
    });
  }
  return sdkLoading;
}

function initFor(appId: string): void {
  if (initializedAppId !== appId) {
    FB.init({ appId, autoLogAppEvents: true, xfbml: true, version: GRAPH_API_VERSION });
    initializedAppId = appId;
  }
}

/** Loads the SDK (once) and initializes it for `appId`. Safe to call repeatedly. */
export async function prepareFacebookSdk(appId: string): Promise<void> {
  await loadSdk();
  initFor(appId);
}

/**
 * The SDK initialized for `appId`, synchronously — `null` while it has not finished loading.
 * Re-initializes on the spot when another app was the last one initialized.
 */
export function facebookSdkFor(appId: string): any | null {
  if (!isSdkLoaded()) {
    return null;
  }
  initFor(appId);
  return FB;
}
